import { type FormEvent, useEffect, useRef, useState } from 'react';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { Settings, ShieldAlert } from 'lucide-react';
import MainLayout from '../../components/layout/MainLayout';
import { Input } from '../../components/ui/Input';
import { db, functions } from '../../config/firebase';
import { useCambiosSinGuardar } from '../../hooks/useCambiosSinGuardar';
import BarraCambiosSinGuardar from '../../components/ui/BarraCambiosSinGuardar';
import { useToast } from '../../contexts/ToastContext';
import { useAuth } from '../../contexts/AuthContext';
import styles from './ConfigGlobalPage.module.css';

/**
 * Proveedor de correo (conmutador GPP-202602). Documento `configuracion/correo`, aparte
 * de `configuracion/global`: se guarda de inmediato al cambiarlo (no espera al botón
 * Guardar general), con quién/cuándo, y las functions lo aplican en < 60 s (RN-01).
 */
type ProveedorCorreo = 'OFFICE365' | 'NOTIFICACIONES';

interface ConfigGlobal {
  nombreSistema: string;
  emailContacto: string;
  permitirAutoRegistro: boolean;
  diasExpiracionPassword: number;
  maxIntentosLogin: number;
  minutosBloqueoLogin: number;
  grabacionHabilitada: boolean;
  iaHabilitada: boolean;
  /** Feature flag global que controla si la app móvil puede usar teleconsulta. */
  teleconsultaMovilHabilitada: boolean;
  /** Si al tomar un paciente se crea la cita en SEIS. Apagado por default. */
  enviarCitasSeis: boolean;
  /** Declaración jurada que el paciente acepta antes del triage (app móvil). */
  declaracionTemplate: string;
  /** Consentimiento (por secciones) que el paciente acepta antes de la teleconsulta. */
  consentimientoSecciones: SeccionConsentimiento[];
}

/** Una sección del consentimiento. `cuerpo` admite **negrita** con dobles asteriscos. */
export interface SeccionConsentimiento {
  titulo: string;
  cuerpo: string;
  /** 'normal' = tarjeta neutra; 'emergencia' = recuadro rojo (resaltado). */
  tipo: 'normal' | 'emergencia';
}

/** Placeholders de sesión que la app resuelve al mostrar la declaración. */
const PLACEHOLDERS_DECLARACION = [
  { token: '{{nombre}}', label: 'Nombre del paciente' },
  { token: '{{cedula}}', label: 'Cédula' },
  { token: '{{fecha}}', label: 'Fecha' },
  { token: '{{hora}}', label: 'Hora' },
  { token: '{{servicio}}', label: 'Nombre del servicio' },
];

const DECLARACION_DEFAULT = `Declaro, bajo la gravedad de juramento, que toda la información personal, administrativa y clínica suministrada a través de esta plataforma es verdadera, completa, exacta y corresponde a mi situación actual.

Reconozco que la atención médica brindada mediante teleconsulta se fundamenta en la información que proporciono, por lo que cualquier omisión, falsedad, inexactitud o suministro intencional de datos incorrectos puede afectar la calidad de la evaluación médica, el diagnóstico, las recomendaciones terapéuticas y mi seguridad como paciente. En consecuencia, asumo la responsabilidad por la veracidad de la información aportada y acepto las implicaciones legales y administrativas que pudieran derivarse de la presentación de información falsa o engañosa, de conformidad con la legislación vigente.`;

const CONSENTIMIENTO_DEFAULT_SECCIONES: SeccionConsentimiento[] = [
  {
    tipo: 'normal',
    titulo: '1. Naturaleza del servicio',
    cuerpo:
      'La teleconsulta es una consulta médica realizada a distancia mediante tecnología de audio y video. Un profesional de salud debidamente autorizado atenderá su consulta.',
  },
  {
    tipo: 'normal',
    titulo: '2. Limitaciones',
    cuerpo:
      '• La teleconsulta **no sustituye** la atención médica presencial ni los servicios de urgencias.\n' +
      '• El médico no podrá realizarle un examen físico directo, lo que puede limitar la evaluación de su condición.\n' +
      '• Pueden ocurrir interrupciones técnicas (pérdida de conexión, calidad de audio/video) que afecten la consulta.\n' +
      '• No todas las condiciones médicas pueden ser evaluadas o tratadas por teleconsulta. El profesional puede referirle a atención presencial si lo considera necesario.\n' +
      '• Bajo esta modalidad **no se emiten incapacidades médicas** (certificados de reposo o de ausencia laboral).',
  },
  {
    tipo: 'emergencia',
    titulo: '3. Emergencias',
    cuerpo:
      'Este servicio **NO es para emergencias médicas**. Si presenta dolor en el pecho, dificultad para respirar, sangrado abundante, pérdida de conciencia u otra situación que ponga en riesgo su vida, llame al **9-1-1** o acuda al centro de urgencias más cercano.',
  },
  {
    tipo: 'normal',
    titulo: '4. Pre-evaluación asistida por inteligencia artificial',
    cuerpo:
      'Antes de su consulta, un asistente virtual le realizará preguntas sobre sus síntomas para ayudar al médico a prepararse. Esta pre-evaluación es **orientativa y no constituye un diagnóstico médico**. Toda la información será revisada por el profesional de salud que lo atienda.',
  },
  {
    tipo: 'normal',
    titulo: '5. Privacidad y datos personales',
    cuerpo:
      'La teleconsulta **será grabada** como parte de la prestación del servicio. Su información personal y de salud, incluida la grabación, será tratada de forma confidencial y se utilizará exclusivamente para la prestación del servicio de teleconsulta, siendo accesible únicamente por el personal médico autorizado. Usted tiene derecho a acceder, rectificar o solicitar la eliminación de sus datos personales.',
  },
  {
    tipo: 'normal',
    titulo: '6. Consentimiento voluntario',
    cuerpo:
      'Su participación es completamente voluntaria. Puede cancelar la teleconsulta en cualquier momento sin que esto afecte su derecho a recibir atención médica por otros medios.',
  },
];

const DEFAULTS: ConfigGlobal = {
  nombreSistema: 'Minsa PdP',
  emailContacto: 'soporte@minsa.gob.pa',
  permitirAutoRegistro: false,
  diasExpiracionPassword: 90,
  maxIntentosLogin: 5,
  minutosBloqueoLogin: 15,
  grabacionHabilitada: false,
  iaHabilitada: true,
  // Habilitada por defecto: la app móvil solo apaga teleconsulta con activo:false
  // explícito (ver getConfig/subscribeConfig en teleconsulta.service.ts). El
  // toggle debe reflejar esa misma semántica cuando el doc aún no existe.
  teleconsultaMovilHabilitada: true,
  enviarCitasSeis: false,
  declaracionTemplate: DECLARACION_DEFAULT,
  consentimientoSecciones: CONSENTIMIENTO_DEFAULT_SECCIONES,
};

export default function ConfigGlobalPage() {
  const { addToast } = useToast();
  const { user, isAdmin } = useAuth();
  const [config, setConfig] = useState<ConfigGlobal>(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // Proveedor de correo (conmutador GPP-202602) — estado y metadatos propios.
  const [proveedorCorreo, setProveedorCorreo] = useState<ProveedorCorreo>('OFFICE365');
  const [correoMeta, setCorreoMeta] = useState<{ por?: string; en?: Date } | null>(null);
  const [guardandoCorreo, setGuardandoCorreo] = useState(false);
  // `configuracion/correo` es admin-only, pero esta pantalla es del supervisor:
  // si la lectura no pasó, la sección no se muestra en lugar de ofrecer un
  // control que fallaría al guardarse.
  const [puedeVerCorreo, setPuedeVerCorreo] = useState(false);
  // Versión vigente de la declaración jurada (no cuenta como "cambio").
  const [declaracionVersion, setDeclaracionVersion] = useState(1);
  const declaracionRef = useRef<HTMLTextAreaElement>(null);
  // Versión vigente del consentimiento (mismo tratamiento que la declaración).
  const [consentimientoVersion, setConsentimientoVersion] = useState(1);

  /* ── Purga del contenido clínico ───────────────────────────────────────────
   * Borra lo que se dijo y se grabó en las teleconsultas pasadas —transcripción,
   * nota SOAPE, grabaciones, análisis— conservando la consulta y su estadística.
   * El borrado es IRREVERSIBLE, así que el encendido, los plazos y el botón
   * manual viven juntos y con confirmación explícita. */
  /* ── Doble factor para todo el portal ──────────────────────────────────────
     Documento aparte (`config/segundoFactor`) que el navegador no puede
     escribir: el cambio pasa por el backend, que exige motivo al apagarlo y
     deja en la auditoría quién lo apagó y quién lo encendió. */
  const [dosFactores, setDosFactores] = useState<{
    desactivado: boolean; motivo?: string | null; por?: string | null; en?: Date;
  }>({ desactivado: false });
  const [guardandoDosFactores, setGuardandoDosFactores] = useState(false);

  const cambiarDosFactores = async (desactivar: boolean) => {
    let motivo = '';
    if (desactivar) {
      const respuesta = window.prompt(
        'ATENCIÓN: desactivar el doble factor deja vulnerable la seguridad del portal.\n\n' +
        'Nadie —incluidos administradores, supervisores y visores— tendrá que ingresar el ' +
        'código de su aplicación al entrar: bastará con la contraseña o el PIN del correo. ' +
        'Una contraseña robada o filtrada daría acceso directo a expedientes y a la ' +
        'administración del sistema.\n\n' +
        'Quedará registrado en la bitácora que usted lo desactivó. Escriba el motivo para continuar:',
      );
      if (respuesta === null) return;
      motivo = respuesta.trim();
      if (motivo.length < 10) {
        addToast('error', 'Escriba un motivo de al menos 10 caracteres.');
        return;
      }
    } else if (!window.confirm(
      'Se volverá a pedir el doble factor a todos los usuarios, excepto a quienes tengan una ' +
      'exención propia. Quien todavía no lo haya configurado deberá hacerlo en su próximo ingreso.\n\n' +
      'Quedará registrado en la bitácora que usted lo activó. ¿Continuar?',
    )) {
      return;
    }

    setGuardandoDosFactores(true);
    try {
      const fn = httpsCallable(functions, 'portal_configurarSegundoFactorGlobal');
      await fn({ desactivado: desactivar, motivo });
      setDosFactores({
        desactivado: desactivar, motivo: motivo || null,
        por: user?.displayName ?? user?.email ?? null, en: new Date(),
      });
      addToast(desactivar ? 'warning' : 'success', desactivar
        ? 'Doble factor desactivado para todos los usuarios.'
        : 'Doble factor activado de nuevo para todos los usuarios.');
    } catch (err) {
      addToast('error', `No se pudo cambiar el doble factor: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setGuardandoDosFactores(false);
    }
  };

  const [purga, setPurga] = useState({ habilitada: false, frecuenciaDias: 1, retencionDias: 7 });
  const [purgaMeta, setPurgaMeta] = useState<{ en?: Date; ultimo?: Record<string, number> } | null>(null);
  const [guardandoPurga, setGuardandoPurga] = useState(false);
  const [purgando, setPurgando] = useState(false);

  const guardarPurga = async (cambios: Partial<typeof purga>) => {
    const siguiente = { ...purga, ...cambios };
    setPurga(siguiente);
    setGuardandoPurga(true);
    try {
      await setDoc(doc(db, 'config', 'purgaClinica'), {
        ...siguiente,
        actualizadoEn: serverTimestamp(),
        actualizadoPor: user?.email ?? 'desconocido',
      }, { merge: true });
      addToast('success', 'Configuración de purga guardada.');
    } catch (err) {
      addToast('error', `No se pudo guardar: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setGuardandoPurga(false);
    }
  };

  const ejecutarPurga = async () => {
    // Confirmación explícita y con el plazo a la vista: quien la ejecuta debe
    // saber exactamente qué antigüedad va a borrar, no solo que "va a limpiar".
    const ok = window.confirm(
      `Se borrará DEFINITIVAMENTE el contenido clínico de las teleconsultas cerradas ` +
      `hace más de ${purga.retencionDias} días: la transcripción, la nota médica, las ` +
      `grabaciones, el análisis de la conversación y las preguntas del triage. También las ` +
      `fotos de la validación biométrica (selfie y documento) que se conservaron para revisión ` +
      `y ya superaron ese plazo.\n\n` +
      `Se conserva todo lo que el paciente ve en su aplicación: el resumen de su médico, ` +
      `el motivo de consulta, el seguimiento y su historial de triage. También la consulta ` +
      `y toda su estadística.\n\nEsta acción NO se puede deshacer. ¿Continuar?`,
    );
    if (!ok) return;
    setPurgando(true);
    try {
      const fn = httpsCallable(functions, 'portal_purgarContenidoClinico');
      const r = (await fn()).data as Record<string, number>;
      setPurgaMeta({ en: new Date(), ultimo: r });
      addToast('success',
        `Purga completada: ${r.consultasPurgadas} consultas, ${r.documentosBorrados} documentos, ${r.audiosBorrados} audios y ${r.imagenesBiometricasBorradas ?? 0} imágenes biométricas.`);
    } catch (err) {
      addToast('error', `Falló la purga: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setPurgando(false);
    }
  };
  // Aviso estándar de cambios pendientes (barra fija al pie + beforeunload).
  const { hayCambios, marcarGuardado, valorGuardado } = useCambiosSinGuardar(config, {
    habilitado: !loading,
  });

  useEffect(() => {
    async function load() {
      try {
        // Lectura tolerante, NO todo-o-nada. Estos documentos tienen permisos
        // distintos por rol: `configuracion/correo` es admin-only mientras que
        // esta pantalla es exclusiva del supervisor. Con un Promise.all, esa
        // única denegación tumbaba la carga entera, nunca se fijaba la línea
        // base de cambios y la barra de Guardar no aparecía jamás — la pantalla
        // quedaba inutilizable sin un solo mensaje de error.
        const leer = async (col: string, id: string) => {
          try {
            return await getDoc(doc(db, col, id));
          } catch (err) {
            console.warn(`[ConfigGlobal] sin acceso a ${col}/${id}; se omite`, err);
            return null;
          }
        };
        const [globalSnap, teleconsultaSnap, declaracionSnap, consentimientoSnap, correoSnap, purgaSnap, dosFactoresSnap] =
          await Promise.all([
            leer('configuracion', 'global'),
            leer('config', 'teleconsulta'),
            leer('config', 'declaracionJurada'),
            leer('config', 'consentimientoTeleconsulta'),
            leer('configuracion', 'correo'),
            leer('config', 'purgaClinica'),
            leer('config', 'segundoFactor'),
          ]);
        if (dosFactoresSnap?.exists()) {
          const d = dosFactoresSnap.data() as {
            desactivado?: boolean; motivo?: string | null; cambiadoPorNombre?: string | null;
            cambiadoEn?: { toDate?: () => Date };
          };
          setDosFactores({
            desactivado: d.desactivado === true, motivo: d.motivo,
            por: d.cambiadoPorNombre, en: d.cambiadoEn?.toDate?.(),
          });
        }
        // Purga: si el documento no existe todavía, se muestran los valores por
        // defecto del backend — apagada, diaria, 7 días de retención.
        if (purgaSnap?.exists()) {
          const pc = purgaSnap.data() as {
            habilitada?: boolean; frecuenciaDias?: number; retencionDias?: number;
            ultimaEjecucion?: { toDate?: () => Date };
            ultimoResultado?: Record<string, number>;
          };
          setPurga({
            habilitada: pc.habilitada === true,
            frecuenciaDias: Number(pc.frecuenciaDias) > 0 ? Number(pc.frecuenciaDias) : 1,
            retencionDias: Number(pc.retencionDias) > 0 ? Number(pc.retencionDias) : 7,
          });
          if (pc.ultimaEjecucion?.toDate) {
            setPurgaMeta({ en: pc.ultimaEjecucion.toDate(), ultimo: pc.ultimoResultado });
          }
        }
        // Solo el admin puede leer el conmutador de proveedor de correo. Si no
        // llegó, la sección no se muestra en vez de fingir un valor por defecto.
        setPuedeVerCorreo(correoSnap !== null);
        if (correoSnap?.exists()) {
          const c = correoSnap.data() as { proveedor?: string; cambiadoPor?: string; cambiadoEn?: { toDate?: () => Date } };
          setProveedorCorreo(c.proveedor === 'NOTIFICACIONES' ? 'NOTIFICACIONES' : 'OFFICE365');
          setCorreoMeta({ por: c.cambiadoPor, en: c.cambiadoEn?.toDate?.() });
        }
        const merged: Partial<ConfigGlobal> = {};
        if (globalSnap?.exists()) {
          Object.assign(merged, globalSnap.data() as Partial<ConfigGlobal>);
        }
        if (teleconsultaSnap?.exists()) {
          const t = teleconsultaSnap.data() as { activo?: boolean; enviarCitasSeis?: boolean };
          // Misma semántica que la app: solo apaga con activo:false explícito.
          merged.teleconsultaMovilHabilitada = t.activo !== false;
          merged.enviarCitasSeis = t.enviarCitasSeis === true;
        }
        if (declaracionSnap?.exists()) {
          const dj = declaracionSnap.data() as { template?: string; version?: number };
          if (dj.template) merged.declaracionTemplate = dj.template;
          if (typeof dj.version === 'number') setDeclaracionVersion(dj.version);
        }
        if (consentimientoSnap?.exists()) {
          const cs = consentimientoSnap.data() as { secciones?: SeccionConsentimiento[]; version?: number };
          if (Array.isArray(cs.secciones) && cs.secciones.length > 0) {
            merged.consentimientoSecciones = cs.secciones.map((s) => ({
              titulo: String(s.titulo ?? ''),
              cuerpo: String(s.cuerpo ?? ''),
              tipo: s.tipo === 'emergencia' ? 'emergencia' : 'normal',
            }));
          }
          if (typeof cs.version === 'number') setConsentimientoVersion(cs.version);
        }
        const cargada = { ...DEFAULTS, ...merged };
        setConfig(cargada);
        // Línea base: a partir de aquí cualquier edición cuenta como pendiente.
        marcarGuardado(cargada);
      } catch (err) {
        console.error('Error cargando config:', err);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  const updateField = <K extends keyof ConfigGlobal>(key: K, value: ConfigGlobal[K]) => {
    setConfig((c) => ({ ...c, [key]: value }));
  };

  // ── Editor de secciones del consentimiento ──
  const updateSeccion = (i: number, campo: keyof SeccionConsentimiento, valor: string) => {
    setConfig((c) => {
      const secciones = c.consentimientoSecciones.map((s, idx) =>
        idx === i ? { ...s, [campo]: valor } : s,
      );
      return { ...c, consentimientoSecciones: secciones };
    });
  };
  const addSeccion = () => {
    setConfig((c) => ({
      ...c,
      consentimientoSecciones: [
        ...c.consentimientoSecciones,
        { titulo: `${c.consentimientoSecciones.length + 1}. Nueva sección`, cuerpo: '', tipo: 'normal' },
      ],
    }));
  };
  const removeSeccion = (i: number) => {
    setConfig((c) => ({
      ...c,
      consentimientoSecciones: c.consentimientoSecciones.filter((_, idx) => idx !== i),
    }));
  };
  const moveSeccion = (i: number, dir: -1 | 1) => {
    setConfig((c) => {
      const j = i + dir;
      if (j < 0 || j >= c.consentimientoSecciones.length) return c;
      const secciones = [...c.consentimientoSecciones];
      [secciones[i], secciones[j]] = [secciones[j], secciones[i]];
      return { ...c, consentimientoSecciones: secciones };
    });
  };

  // Conmutador de proveedor de correo: guarda de inmediato con quién/cuándo (RN-01, RN-04).
  const cambiarProveedorCorreo = async (valor: ProveedorCorreo) => {
    if (valor === proveedorCorreo || guardandoCorreo) return;
    const anterior = proveedorCorreo;
    setProveedorCorreo(valor); // optimista
    setGuardandoCorreo(true);
    try {
      await setDoc(doc(db, 'configuracion', 'correo'), {
        proveedor: valor,
        cambiadoPor: user?.email ?? 'desconocido',
        cambiadoEn: serverTimestamp(),
      }, { merge: true });
      setCorreoMeta({ por: user?.email ?? 'desconocido', en: new Date() });
      addToast(
        'success',
        valor === 'NOTIFICACIONES'
          ? 'Correo por el Servicio de Notificaciones. Aplica en menos de un minuto.'
          : 'Correo por Office 365 (actual).',
      );
    } catch (err) {
      setProveedorCorreo(anterior); // revertir
      addToast('error', 'No se pudo cambiar el proveedor de correo.');
      console.error('Error guardando proveedor de correo:', err);
    } finally {
      setGuardandoCorreo(false);
    }
  };

  const handleSave = async (e?: FormEvent) => {
    e?.preventDefault();
    console.log('[ConfigGlobal] handleSave invocado');
    setSaving(true);
    // Cada paso escribe un documento distinto y con permisos distintos. Si uno
    // falla NO se abandona el resto: antes la primera excepción cortaba la
    // secuencia y lo ya escrito quedaba aplicado sin que el usuario lo supiera
    // — un guardado a medias silencioso, peor que no guardar nada.
    const fallos: string[] = [];
    const paso = async (nombre: string, fn: () => Promise<void>) => {
      try {
        await fn();
        console.log(`[ConfigGlobal] ${nombre} OK`);
      } catch (err) {
        console.error(`[ConfigGlobal] ${nombre} FALLÓ`, err);
        const code = (err as { code?: string })?.code ?? '';
        fallos.push(
          code.includes('permission-denied')
            ? `${nombre} (sin permiso para este rol)`
            : `${nombre} (${err instanceof Error ? err.message : String(err)})`,
        );
      }
    };

    try {
      // 1. Guardar config general (sin los campos de teleconsulta ni la
      // declaración jurada, que viven en sus propios documentos)
      const { teleconsultaMovilHabilitada, enviarCitasSeis, declaracionTemplate, consentimientoSecciones, ...globalConfig } = config;
      await paso('Configuración general', async () => {
        await setDoc(doc(db, 'configuracion', 'global'), {
          ...globalConfig,
          teleconsultaMovilHabilitada,
          actualizadoEn: serverTimestamp(),
        });
      });

      // 2. Guardar feature flag de teleconsulta móvil (doc separado que lee la app)
      await paso('Interruptor de teleconsulta', async () => {
        await setDoc(
          doc(db, 'config', 'teleconsulta'),
          {
            activo: teleconsultaMovilHabilitada,
            enviarCitasSeis,
            actualizadoEn: serverTimestamp(),
          },
          { merge: true },
        );
      });

      // 2b/2c. Textos legales CON VERSIONAMIENTO (declaración jurada y
      // consentimiento). Ya NO se escriben desde aquí: la versión exacta que
      // el paciente aceptó es evidencia, y archivar la saliente + publicar la
      // nueva debe ser atómico. Antes eran escrituras sueltas del navegador y
      // una interrupción a mitad dejaba el historial incoherente en silencio.
      // El callable las hace en un batch y valida el rol del lado del servidor.
      const previo = valorGuardado();
      const publicar = httpsCallable(functions, 'portal_publicarTextoLegal');

      if (!previo || previo.declaracionTemplate !== declaracionTemplate) {
        await paso('Declaración jurada', async () => {
          const r = await publicar({ tipo: 'declaracionJurada', template: declaracionTemplate });
          const d = r.data as { sinCambios: boolean; version: number };
          setDeclaracionVersion(d.version);
        });
      }

      const seccionesJSON = JSON.stringify(consentimientoSecciones);
      if (!previo || JSON.stringify(previo.consentimientoSecciones) !== seccionesJSON) {
        await paso('Consentimiento', async () => {
          const r = await publicar({ tipo: 'consentimientoTeleconsulta', secciones: consentimientoSecciones });
          const d = r.data as { sinCambios: boolean; version: number };
          setConsentimientoVersion(d.version);
        });
      }

      // 3. Verificar contra el servidor que el interruptor quedó como se pidió.
      // Se lee de vuelta a propósito: un `setDoc` que no lanza no garantiza que
      // el valor sea el esperado si otra escritura lo pisó.
      let verifyActivo: boolean | undefined;
      await paso('Verificación del interruptor', async () => {
        const verifySnap = await getDoc(doc(db, 'config', 'teleconsulta'));
        verifyActivo = verifySnap.data()?.activo;
        if (verifyActivo !== teleconsultaMovilHabilitada) {
          throw new Error(`quedó en ${verifyActivo}, se esperaba ${teleconsultaMovilHabilitada}`);
        }
      });

      if (fallos.length === 0) {
        marcarGuardado();
        addToast('success', `Configuración guardada. Teleconsulta móvil: ${teleconsultaMovilHabilitada ? 'Activa' : 'Inactiva'}.`);
      } else {
        // No se marca como guardado: la barra sigue visible para que el cambio
        // no se dé por aplicado y se pueda reintentar.
        addToast('error', `No se guardó todo. Falló: ${fallos.join(' · ')}`);
      }
    } catch (err) {
      console.error('[ConfigGlobal] Error inesperado guardando config:', err);
      addToast('error', `Error al guardar: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <MainLayout><p>Cargando configuracion...</p></MainLayout>;

  return (
    <MainLayout>
      <div className={styles.page}>
        <div className={styles.header}>
          <Settings size={22} />
          <h1 className={styles.title}>Configuracion Global</h1>
        </div>

        <form onSubmit={handleSave} className={styles.sections}>
          {/* General */}
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>General</h2>
            <div className={styles.grid}>
              <Input label="Nombre del sistema" value={config.nombreSistema}
                onChange={(e) => updateField('nombreSistema', e.target.value)} />
              <Input label="Email de contacto" type="email" value={config.emailContacto}
                onChange={(e) => updateField('emailContacto', e.target.value)} />
            </div>
          </section>

          {/* Proveedor de correo — conmutador GPP-202602 (solo admin).
              Se exige además que la LECTURA haya pasado: el rol es condición
              necesaria pero no suficiente, y mostrar el control sin haber
              podido leer el documento ofrecería un ajuste que no se guardaría. */}
          {isAdmin && puedeVerCorreo && (
            <section className={styles.section}>
              <h2 className={styles.sectionTitle}>Proveedor de correo</h2>
              <div className={styles.sectionBody}>
                <p className={styles.sectionDescription}>
                  Define por dónde salen los correos del portal y de la app móvil. El cambio
                  aplica en menos de un minuto, sin desplegar nada. Si el nuevo servicio
                  presenta problemas, regrese a Office 365 desde aquí.
                </p>
                <div className={styles.grid}>
                  <label className={styles.toggle}>
                    <input
                      type="radio"
                      name="proveedorCorreo"
                      checked={proveedorCorreo === 'OFFICE365'}
                      disabled={guardandoCorreo}
                      onChange={() => cambiarProveedorCorreo('OFFICE365')}
                    />
                    Office 365 (actual)
                  </label>
                  <label className={styles.toggle}>
                    <input
                      type="radio"
                      name="proveedorCorreo"
                      checked={proveedorCorreo === 'NOTIFICACIONES'}
                      disabled={guardandoCorreo}
                      onChange={() => cambiarProveedorCorreo('NOTIFICACIONES')}
                    />
                    Servicio de Notificaciones
                  </label>
                </div>
                {correoMeta?.por && (
                  <p className={styles.sectionDescription}>
                    Proveedor actual:{' '}
                    <strong>
                      {proveedorCorreo === 'NOTIFICACIONES' ? 'Servicio de Notificaciones' : 'Office 365'}
                    </strong>{' '}
                    — cambiado por {correoMeta.por}
                    {correoMeta.en ? ` el ${correoMeta.en.toLocaleString('es-PA')}` : ''}
                  </p>
                )}
              </div>
            </section>
          )}

          {/* Consultas */}
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Consultas</h2>
            {/* CAP-01: "Max pacientes por sala" y "Tiempo consulta default"
                se eliminaron — eran config global huérfana (nunca se leía). Estos
                valores se configuran POR SALA en Configurar salas → Capacidad. */}
            <div className={styles.grid}>
              <label className={styles.toggle}>
                <input type="checkbox" checked={config.grabacionHabilitada}
                  onChange={(e) => updateField('grabacionHabilitada', e.target.checked)} />
                Grabacion de consultas habilitada
              </label>
              <label className={styles.toggle}>
                <input type="checkbox" checked={config.iaHabilitada}
                  onChange={(e) => updateField('iaHabilitada', e.target.checked)} />
                Notas IA habilitadas
              </label>
            </div>
          </section>

          {/* Módulo móvil de teleconsulta */}
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>App móvil — Teleconsulta</h2>
            <div className={styles.sectionBody}>
              <p className={styles.sectionDescription}>
                Feature flag global que controla si la app móvil MedisysPdP
                muestra el módulo de teleconsulta al paciente. Si está
                <strong> deshabilitado</strong>, la app muestra
                <em> &ldquo;No disponible&rdquo; </em>
                independientemente de las salas y horarios configurados.
              </p>

              <label className={styles.featureToggle}>
                <input
                  type="checkbox"
                  checked={config.teleconsultaMovilHabilitada}
                  onChange={(e) =>
                    updateField('teleconsultaMovilHabilitada', e.target.checked)
                  }
                />
                <div className={styles.featureToggleMain}>
                  <span className={styles.featureToggleTitle}>
                    Habilitar teleconsulta en la app móvil
                  </span>
                  <span
                    className={`${styles.featureStatus} ${
                      config.teleconsultaMovilHabilitada
                        ? styles.featureStatusActive
                        : ''
                    }`}
                  >
                    <span className={styles.featureStatusDot} aria-hidden="true" />
                    {config.teleconsultaMovilHabilitada
                      ? 'Activa — los pacientes pueden solicitar consulta'
                      : 'Inactiva — los pacientes ven "No disponible"'}
                  </span>
                </div>
              </label>

              {/* Declaración jurada — visible solo con la teleconsulta habilitada */}
              {config.teleconsultaMovilHabilitada && (
                <div className={styles.declaracionBox}>
                  <div className={styles.declaracionHead}>
                    <span className={styles.declaracionTitle}>
                      Declaración jurada del paciente
                    </span>
                    <span className={styles.declaracionVersion}>versión {declaracionVersion}</span>
                  </div>
                  <p className={styles.sectionDescription}>
                    El paciente debe aceptarla después del consentimiento y antes
                    del triage. Cada cambio crea una <strong>nueva versión</strong> y
                    la anterior queda archivada como evidencia. Puede insertar
                    datos de la sesión con los botones:
                  </p>
                  <div className={styles.declaracionChips}>
                    {PLACEHOLDERS_DECLARACION.map((p) => (
                      <button
                        key={p.token}
                        type="button"
                        className={styles.declaracionChip}
                        title={`Insertar ${p.label}`}
                        onClick={() => {
                          const ta = declaracionRef.current;
                          const pos = ta?.selectionStart ?? config.declaracionTemplate.length;
                          const t = config.declaracionTemplate;
                          updateField('declaracionTemplate', t.slice(0, pos) + p.token + t.slice(pos));
                          requestAnimationFrame(() => {
                            ta?.focus();
                            ta?.setSelectionRange(pos + p.token.length, pos + p.token.length);
                          });
                        }}
                      >
                        {p.label}
                      </button>
                    ))}
                  </div>
                  <textarea
                    ref={declaracionRef}
                    className={styles.declaracionTextarea}
                    value={config.declaracionTemplate}
                    onChange={(e) => updateField('declaracionTemplate', e.target.value)}
                    rows={12}
                    spellCheck
                  />
                </div>
              )}

              {/* Consentimiento — editor POR SECCIONES (visible con teleconsulta habilitada) */}
              {config.teleconsultaMovilHabilitada && (
                <div className={styles.declaracionBox}>
                  <div className={styles.declaracionHead}>
                    <span className={styles.declaracionTitle}>
                      Consentimiento de teleconsulta
                    </span>
                    <span className={styles.declaracionVersion}>versión {consentimientoVersion}</span>
                  </div>
                  <p className={styles.sectionDescription}>
                    El paciente debe aceptarlo antes de iniciar la teleconsulta.
                    Cada sección tiene un título y un texto; puede marcarla como{' '}
                    <strong>Emergencia</strong> para que la app la resalte en rojo.
                    Use <code>**texto**</code> para poner algo en <strong>negrita</strong>.
                    Cada cambio crea una nueva versión y archiva la anterior.
                  </p>

                  <div className={styles.consentSecciones}>
                    {config.consentimientoSecciones.map((sec, i) => (
                      <div
                        key={i}
                        className={`${styles.consentSeccion} ${sec.tipo === 'emergencia' ? styles.consentSeccionEmergencia : ''}`}
                      >
                        <div className={styles.consentSeccionHead}>
                          <span className={styles.consentSeccionNum}>Sección {i + 1}</span>
                          <div className={styles.consentSeccionAcciones}>
                            <label className={styles.consentTipoLabel}>
                              <input
                                type="checkbox"
                                checked={sec.tipo === 'emergencia'}
                                onChange={(e) =>
                                  updateSeccion(i, 'tipo', e.target.checked ? 'emergencia' : 'normal')
                                }
                              />
                              Emergencia (rojo)
                            </label>
                            <button type="button" title="Subir" className={styles.consentIconBtn}
                              disabled={i === 0} onClick={() => moveSeccion(i, -1)}>↑</button>
                            <button type="button" title="Bajar" className={styles.consentIconBtn}
                              disabled={i === config.consentimientoSecciones.length - 1}
                              onClick={() => moveSeccion(i, 1)}>↓</button>
                            <button type="button" title="Eliminar sección"
                              className={`${styles.consentIconBtn} ${styles.consentIconBtnDanger}`}
                              onClick={() => removeSeccion(i)}>✕</button>
                          </div>
                        </div>
                        <input
                          className={styles.consentTituloInput}
                          value={sec.titulo}
                          placeholder="Título de la sección"
                          onChange={(e) => updateSeccion(i, 'titulo', e.target.value)}
                        />
                        <textarea
                          className={styles.consentSeccionTextarea}
                          value={sec.cuerpo}
                          placeholder="Texto de la sección (use **negrita** y • para viñetas)"
                          onChange={(e) => updateSeccion(i, 'cuerpo', e.target.value)}
                          rows={5}
                          spellCheck
                        />
                      </div>
                    ))}
                  </div>

                  <button type="button" className={styles.consentAddBtn} onClick={addSeccion}>
                    + Agregar sección
                  </button>
                </div>
              )}
            </div>
          </section>

          {/* Integración MINSA (HCIS) — creación de citas */}
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Integración MINSA (HCIS) — Citas</h2>
            <div className={styles.sectionBody}>
              <p className={styles.sectionDescription}>
                Cuando un médico toma un paciente de la cola, el sistema puede
                confirmar automáticamente la cita equivalente en <strong>HCIS</strong> (MINSA).
                Si falla, la teleconsulta continúa y el médico ve un aviso con la opción de
                reintentar. Si está <strong>deshabilitado</strong> (valor por defecto), la cita
                <em> no se envía</em> y en el historial se indica que no se creó
                porque el envío está apagado.
              </p>

              <label className={styles.featureToggle}>
                <input
                  type="checkbox"
                  checked={config.enviarCitasSeis}
                  onChange={(e) => updateField('enviarCitasSeis', e.target.checked)}
                />
                <div className={styles.featureToggleMain}>
                  <span className={styles.featureToggleTitle}>
                    Enviar citas a MINSA (HCIS)
                  </span>
                  <span
                    className={`${styles.featureStatus} ${
                      config.enviarCitasSeis ? styles.featureStatusActive : ''
                    }`}
                  >
                    <span className={styles.featureStatusDot} aria-hidden="true" />
                    {config.enviarCitasSeis
                      ? 'Activo — la cita se confirma en HCIS al tomar al paciente'
                      : 'Inactivo — las citas no se envían a HCIS'}
                  </span>
                </div>
              </label>
            </div>
          </section>

          {/* Seguridad */}
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Seguridad</h2>
            <div className={styles.grid}>
              <Input label="Dias expiracion contrasena" type="number" value={String(config.diasExpiracionPassword)}
                onChange={(e) => updateField('diasExpiracionPassword', Number(e.target.value))} />
              <Input label="Max intentos login" type="number" value={String(config.maxIntentosLogin)}
                onChange={(e) => updateField('maxIntentosLogin', Number(e.target.value))} />
              <Input label="Minutos bloqueo login" type="number" value={String(config.minutosBloqueoLogin)}
                onChange={(e) => updateField('minutosBloqueoLogin', Number(e.target.value))} />
            </div>

            <div className={styles.sectionBody}>
              <label className={styles.featureToggle}>
                <input
                  type="checkbox"
                  checked={!dosFactores.desactivado}
                  disabled={guardandoDosFactores}
                  onChange={(e) => cambiarDosFactores(!e.target.checked)}
                />
                <span className={styles.featureToggleMain}>
                  <span className={styles.featureToggleTitle}>
                    Doble factor de autenticación para todos los usuarios
                  </span>
                  <span className={styles.sectionDescription}>
                    {dosFactores.desactivado
                      ? 'Desactivado: nadie ingresa el código de su aplicación al entrar, tampoco administradores, supervisores ni visores.'
                      : 'Activado: todos deben ingresar el código de su aplicación al entrar, salvo quienes tengan una exención propia.'}
                  </span>
                </span>
              </label>

              <p className={styles.alertaSeguridad}>
                <ShieldAlert size={16} aria-hidden="true" />
                <span>
                  <strong>Desactivar esta opción deja vulnerable la seguridad del portal.</strong>{' '}
                  Bastaría una contraseña robada o filtrada para entrar a expedientes y a la
                  administración del sistema. Úsela solo en una emergencia y vuelva a activarla
                  cuanto antes: al activarla, se pide de nuevo a todos excepto a quienes estén
                  eximidos. Cada cambio queda en la bitácora con quién lo hizo.
                </span>
              </p>

              {dosFactores.en && (
                <p className={styles.sectionDescription}>
                  Último cambio: {dosFactores.desactivado ? 'desactivado' : 'activado'}
                  {dosFactores.por ? ` por ${dosFactores.por}` : ''} el {dosFactores.en.toLocaleString('es-PA')}
                  {dosFactores.desactivado && dosFactores.motivo ? ` — motivo: ${dosFactores.motivo}` : ''}.
                </p>
              )}
            </div>
          </section>

          {/* Purga del contenido clínico — solo supervisor, igual que el resto
              de esta pantalla. El borrado es irreversible: por eso el plazo
              está a la vista y el botón exige confirmación. */}
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Depuración de información médica</h2>
            <div className={styles.sectionBody}>
              <p className={styles.sectionDescription}>
                Borra el contenido de las teleconsultas ya cerradas: la transcripción, la nota
                médica, las grabaciones, el análisis de la conversación y las preguntas del triage,
                además de las fotos de la validación biométrica (selfie y documento) que se
                guardaron para revisión y ya cumplieron el mismo plazo. Las de validaciones aprobadas
                ya se borran solas al aprobarse.
                <strong> Se conserva todo lo que el paciente ve en su aplicación</strong> —el resumen de su
                médico, el motivo de consulta, el plan de seguimiento y su historial de triage— junto
                con la consulta y toda su estadística: fechas, médico, sala, duración y disposición.
                No se toca ninguna información de usuarios ni del registro.
              </p>

              <label className={styles.featureToggle}>
                <input
                  type="checkbox"
                  checked={purga.habilitada}
                  disabled={guardandoPurga}
                  onChange={(e) => guardarPurga({ habilitada: e.target.checked })}
                />
                <span className={styles.featureToggleMain}>
                  <span className={styles.featureToggleTitle}>Depuración automática</span>
                  <span className={styles.sectionDescription}>
                    {purga.habilitada
                      ? `Se ejecuta sola cada ${purga.frecuenciaDias} día(s), a las 7:00 p. m. de Panamá.`
                      : 'Apagada: solo se depura si alguien pulsa el botón de abajo.'}
                  </span>
                </span>
              </label>

              <div className={styles.grid}>
                <Input
                  label="Ejecutar cada (días)"
                  type="number"
                  min={1}
                  value={String(purga.frecuenciaDias)}
                  disabled={guardandoPurga}
                  onChange={(e) => setPurga((p) => ({ ...p, frecuenciaDias: Number(e.target.value) || 1 }))}
                  onBlur={() => guardarPurga({})}
                />
                <Input
                  label="Borrar consultas cerradas hace más de (días)"
                  type="number"
                  min={1}
                  value={String(purga.retencionDias)}
                  disabled={guardandoPurga}
                  onChange={(e) => setPurga((p) => ({ ...p, retencionDias: Number(e.target.value) || 1 }))}
                  onBlur={() => guardarPurga({})}
                />
              </div>

              {purgaMeta?.en && (
                <p className={styles.sectionDescription}>
                  Última ejecución: {purgaMeta.en.toLocaleString('es-PA')}
                  {purgaMeta.ultimo &&
                    ` — ${purgaMeta.ultimo.consultasPurgadas} consultas, ${purgaMeta.ultimo.documentosBorrados} documentos, ${purgaMeta.ultimo.audiosBorrados} audios, ${purgaMeta.ultimo.imagenesBiometricasBorradas ?? 0} imágenes biométricas.`}
                </p>
              )}

              <button
                type="button"
                className={styles.consentAddBtn}
                onClick={ejecutarPurga}
                disabled={purgando}
              >
                {purgando ? 'Depurando…' : 'Depurar ahora'}
              </button>
            </div>
          </section>

          {/* Sin botón Guardar fijo: la acción vive en BarraCambiosSinGuardar,
              que solo aparece cuando hay algo que guardar. Tener ambos mostraba
              dos botones de guardar a la vez. */}
        </form>
      </div>

      <BarraCambiosSinGuardar
        visible={hayCambios}
        guardando={saving}
        onGuardar={() => handleSave()}
        onDescartar={() => {
          const previo = valorGuardado();
          if (previo) setConfig(previo);
        }}
      />
    </MainLayout>
  );
}
